<template>
  <div
    @click="openMediaAsset"
    class="cursor-pointer relative"
    ref="card"
    @mouseover="onMouseOver"
    @mouseout="onMouseOut"
    @mousemove="onMouseMove"
    v-if="asset"
    :title="`${asset?.story ? `Story: ${storyTitle}\\n` : ''}Name: ${asset?.name}\\nOriginal Filename: ${filename}`"
  >
    <!-- DEFAULT / TYPE ICON -->
    <!-- Audio has no backend thumbnail (per ND-1724 decision); the mic
         icon is the final rendering for audio MediaAssets. Video and
         image fall back to this when no thumbnail is set yet. -->
    <div class="w-full bg-cool-gray-300 aspect-video flex justify-center items-center text-4xl font-bold text-cool-gray-500">
      <div v-if="mediaType.isAudio"><mic-icon /></div>
      <div v-else-if="mediaType.isImage"><image-icon /></div>
      <div v-else><video-icon /></div>
    </div>
    <!-- POSTER IMAGE (video + image only — audio never has a signed thumbnail) -->
    <div v-if="!mediaType.isAudio" class="w-full absolute top-0 left-0">
      <img v-if="signedThumbnailUrl" :src="signedThumbnailUrl" draggable="false" class="w-full aspect-video" />
      <div v-else class="w-full aspect-video bg-gray-200 flex items-center justify-center text-gray-400 text-sm">Loading...</div>
    </div>
    <!-- PROXY VIDEO PLAYER -->
    <div class="w-full my-aspect-16/9 absolute top-0 left-0 bg-blue-500" v-show="playerLoaded">
      <video v-if="hasHovered" class="vjs-default-skin video-js vjs-big-play-centered" ref="videoPlayer" :options="playerOptions"></video>
      <!-- PLAYHEAD -->
      <div v-if="isHovered && playerLoaded" class="playhead" ref="playhead"></div>
    </div>
    <!-- INFO  -->
    <div v-if="showLabel" class="bg-cool-gray-700 text-white p-2 px-4 text-center font-semibold rounded-b">
      <!-- <div class="truncate">{{ asset.story.title }}</div> -->
      <div class="truncate">{{ asset.name }}</div>
      <div v-if="asset.name != filename" class="text-gray-400 truncate">{{ filename }}</div>
    </div>
  </div>
  <div v-else class="w-full bg-cool-gray-200 aspect-video flex justify-center items-center text-gray-400">No Asset</div>
</template>

<script>
import videojs from 'video.js'
import { FeatherIcons } from '@/scripps-components'
import api from '@/api'
import thumbnailCache from '@/services/thumbnailCache'
import { useMediaType } from '@/composables/useMediaType'
const { VideoIcon, MicIcon, ImageIcon } = FeatherIcons
export default {
  components: { VideoIcon, MicIcon, ImageIcon },
  props: {
    asset: {
      type: Object,
      default: () => {}
    },
    showLabel: {
      type: Boolean,
      default: false
    }
  },
  emits: ['clicked'],
  data() {
    return {
      player: null,
      playerOptions: {},
      playerLoaded: false,
      isHovered: false,
      hasHovered: false,
      hoverTimer: null,
      signedThumbnailUrl: null,
      loadingThumbnail: false
    }
  },
  watch: {
    isHovered: function (value, old) {
      if (value == old) return
      if (!this.playerLoaded) return
      if (value) this.player.play().catch((error) => {})
      else this.player.pause()
    },
    hasHovered: function (value, old) {
      if (value == old) return
      if (value && !this.player && this.asset.proxy) {
        setTimeout(() => {
          this.playerOptions = {
            autoplay: true,
            controls: false,
            fluid: true,
            aspectRatio: '16:9',
            muted: true,
            poster: this.asset.thumbnail,
            sources: [
              {
                src: this.asset.proxy,
                type: 'video/mp4'
              }
            ]
          }
          try {
            if (this.player) this.player.dispose()
          } catch (error) {}
          this.player = videojs(this.$refs.videoPlayer, this.playerOptions, () => {
            // this.player.log('onPlayerReady')
            setTimeout(() => {
              this.playerLoaded = true
            }, 60)
            this.player.volume(0.5)
          })
        }, 200)
      }
    }
  },
  watch: {
    asset: {
      handler(newAsset) {
        if (newAsset?.id) {
          this.loadThumbnailUrl()
        }
      },
      deep: true
    }
  },
  mounted() {
    videojs.log.level('off')
    if (this.asset?.id) {
      this.loadThumbnailUrl()
    }
  },
  computed: {
    filename() {
      return this.asset?.original_filename
        ? this.asset.original_filename
        : this.asset.original
          ? decodeURIComponent(this.asset.original.split('/').pop(-1))
          : null
    },
    storyTitle() {
      return this.asset?.story?.title ?? '[none]'
    },
    mediaType() {
      return useMediaType(this.asset)
    }
  },
  methods: {
    async loadThumbnailUrl() {
      if (!this.asset?.id) return

      // Check if asset has a thumbnail property before making API call
      if (!this.asset?.thumbnail) return

      // Check cache first
      if (thumbnailCache.has(this.asset.id)) {
        this.signedThumbnailUrl = thumbnailCache.get(this.asset.id)
        return
      }

      // Load from API
      if (!this.loadingThumbnail) {
        this.loadingThumbnail = true
        try {
          const response = await api.newsdesk.getMediaAssetVariantUrl(this.asset.id, 'thumbnail')
          this.signedThumbnailUrl = response?.data?.signedURL

          if (this.signedThumbnailUrl) {
            thumbnailCache.set(this.asset.id, this.signedThumbnailUrl)
          }
        } catch (error) {
          console.warn(`Failed to load thumbnail for asset ${this.asset.id}:`, error)
        } finally {
          this.loadingThumbnail = false
        }
      }
    },
    openMediaAsset(event) {
      this.$emit('clicked', this.asset)
    },
    onMouseOver(event) {
      // Hover-preview requires a playable proxy. Skip for known non-video
      // types (image, audio). Unknown mime falls through so legacy assets
      // with missing mime_type keep behaving as they did pre-composable.
      if (this.mediaType.isImage || this.mediaType.isAudio) return
      this.hoverTimer = setTimeout(() => {
        this.isHovered = true
        this.hasHovered = true
      }, 300)
    },
    onMouseOut(event) {
      clearTimeout(this.hoverTimer)
      this.isHovered = false
    },
    onMouseMove(event) {
      let percent = event.layerX / this.$el.offsetWidth
      if (this.player && this.player.readyState() > 3) {
        let duration = this.player.duration()
        this.player.currentTime(duration * percent)
      }
      if (this.$refs.playhead) {
        this.$refs.playhead.style.left = `${percent * 100}%`
      }
    }
  }
}
</script>

<style lang="postcss" scoped>
.aspect-video {
  aspect-ratio: 16 / 9;
}
.video-js {
  height: 100% !important;
}

:deep(.video-scale) {
  @apply object-contain;
}
:deep(.vjs-poster) {
  aspect-ratio: 16 / 9;
  background-size: 100% 100%;
}
.playhead {
  @apply bg-cool-gray-800;
  position: absolute;
  top: 0;
  bottom: 0;
  width: 2px;
  pointer-events: none; /* So it doesn't interfere with mouse events on the video */
  left: 0; /* Initial position */
}
</style>
